import { useState, useEffect, useCallback } from 'react';
import fetchData from '../functions/fetchData';
import { toast } from 'react-toastify';

/**
 * Custom hook to manage billing date based on last closed date
 * Fetches the last closed date from day_close_summary table and increments by 1 day
 * @returns {Object} - { billingDate, isLoading, refreshBillingDate, formatBillingDate }
 */
const useBillingDate = () => {
  const [billingDate, setBillingDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Format date to YYYY-MM-DD for database operations
  const formatDateForDB = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Format date for display (DD/MM/YYYY)
  const formatBillingDate = (date = billingDate) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Calculate next business day based on last closed date
  const calculateBillingDate = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch the last closed date from day_close_summary table
      // Order by close_date DESC to get the latest closed date
      const lastClosedData = await fetchData(
        'day_close_summary', 
        null, 
        'close_date', 
        {}, 
        'close_date DESC LIMIT 1'
      );

      let nextBillingDate;

      if (lastClosedData && lastClosedData.length > 0) {
        // Get the last closed date and add 1 day
        const lastClosedDate = new Date(lastClosedData[0].close_date);
        nextBillingDate = new Date(lastClosedDate);
        nextBillingDate.setDate(lastClosedDate.getDate() + 1);
        
        console.log('Last closed date:', formatDateForDB(lastClosedDate));
        console.log('Next billing date:', formatDateForDB(nextBillingDate));
      } else {
        // If no closed dates found, use current system date
        nextBillingDate = new Date();
        console.log('No previous closed dates found, using current date:', formatDateForDB(nextBillingDate));
      }

      const formattedDate = formatDateForDB(nextBillingDate);
      setBillingDate(formattedDate);

      return formattedDate;

    } catch (error) {
      console.error('Error calculating billing date:', error);
      
      // Fallback to current system date on error
      const fallbackDate = formatDateForDB(new Date());
      setBillingDate(fallbackDate);
      
      toast.error('Error fetching billing date, using current date as fallback');
      return fallbackDate;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh billing date function
  const refreshBillingDate = useCallback(async () => {
    return await calculateBillingDate();
  }, [calculateBillingDate]);

  // Initialize billing date on component mount
  useEffect(() => {
    calculateBillingDate();
  }, [calculateBillingDate]);

  // Get billing date as Date object
  const getBillingDateObject = useCallback(() => {
    return billingDate ? new Date(billingDate) : new Date();
  }, [billingDate]);

  // Check if a date is the current billing date
  const isBillingDate = useCallback((dateToCheck) => {
    if (!billingDate || !dateToCheck) return false;
    const checkDate = typeof dateToCheck === 'string' ? dateToCheck : formatDateForDB(new Date(dateToCheck));
    return checkDate === billingDate;
  }, [billingDate]);

  // Get previous billing date (current billing date - 1)
  const getPreviousBillingDate = useCallback(() => {
    if (!billingDate) return null;
    const prevDate = new Date(billingDate);
    prevDate.setDate(prevDate.getDate() - 1);
    return formatDateForDB(prevDate);
  }, [billingDate]);

  return {
    billingDate,
    isLoading,
    refreshBillingDate,
    formatBillingDate,
    getBillingDateObject,
    isBillingDate,
    getPreviousBillingDate,
    formatDateForDB
  };
};

export default useBillingDate;
